import { ReactNode } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { Employee } from '../../types/employee.types';
import { EmployeeSummaryRail } from './employee-summary-rail';
import { ProfileHeader } from './profile-header';

export interface ProfileTab<TTab extends string> {
  value: TTab;
  label: string;
}

export interface EmployeeProfileViewProps<TTab extends string> {
  employee: Employee;
  /** The page's buttons in the header (HR term, Edit, Edit profile). */
  actions?: ReactNode;
  tabs: ProfileTab<TTab>[];
  tab: TTab;
  onTabChange: (tab: TTab) => void;
  /** The open tab's content. */
  children: ReactNode;
}

/**
 * Employee details and My Profile (Component Map › Employee details, Option A): the header, then the
 * summary rail beside one tab row and the open tab. Below 1024px the rail stacks above the tabs.
 */
export const EmployeeProfileView = <TTab extends string>({
  employee,
  actions,
  tabs,
  tab,
  onTabChange,
  children,
}: EmployeeProfileViewProps<TTab>) => (
  <div className="flex w-full flex-col gap-6">
    <ProfileHeader employee={employee} actions={actions} />
    <div className="grid items-start gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
      <EmployeeSummaryRail employee={employee} />
      <Tabs value={tab} onValueChange={(value) => onTabChange(value as TTab)} className="min-w-0">
        <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
          {tabs.map((item) => (
            <TabsTrigger key={item.value} value={item.value} className="max-sm:flex-1">
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={tab} className="mt-3">
          {children}
        </TabsContent>
      </Tabs>
    </div>
  </div>
);
